// Position and radius quantisation (protocol §2). `pos` = round(value / posQ) as i16 and
// `rad` = round(r / radQ) as u16; both steps are announced in WELCOME.

import { DEFAULT_POS_Q, DEFAULT_RAD_Q } from '../constants.js';

/** The quantisation steps a client learned from WELCOME. */
export interface Quant {
  readonly posQ: number;
  readonly radQ: number;
}

export const DEFAULT_QUANT: Quant = Object.freeze({ posQ: DEFAULT_POS_Q, radQ: DEFAULT_RAD_Q });

/** Quantised position: `round(value / posQ)`, clamped to the i16 range. */
export function quantizePos(value: number, posQ: number): number {
  const q = Math.round(value / posQ);
  return q < -32768 ? -32768 : q > 32767 ? 32767 : q;
}

/** Quantised radius: `round(r / radQ)`, clamped to 0–65535. */
export function quantizeRad(r: number, radQ: number): number {
  const q = Math.round(r / radQ);
  return q < 0 ? 0 : q > 65535 ? 65535 : q;
}

/**
 * The server's `posQ` for a map of the given half-width (protocol §2): the smallest of 0.25, 0.5,
 * 1, 2, … for which `halfWidth + 100` fits in i16 (0.25 up to 8,000 u, 0.5 up to 16,000, 1 up to
 * 32,000).
 */
export function choosePosQ(halfWidth: number): number {
  let q = DEFAULT_POS_Q;
  while (halfWidth > 32000 * q) q *= 2;
  return q;
}

/** A fraction sent as `u16 × 10000` (team shares, view scale): rounded, clamped to 0–65535. */
export function fractionToWire(v: number): number {
  const q = Math.round(v * 10000);
  return q < 0 ? 0 : q > 65535 ? 65535 : q;
}

/** The inverse of `fractionToWire`. */
export function fractionFromWire(n: number): number {
  return n / 10000;
}
